import type { CompRef } from '@wix/document-services-types';
import { ManifestStore } from '../../stores';

import {
  ChangeType,
  IManifestChangeDetection,
  ManifestChangeDetection,
} from '../../ManifestChangeDetection';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { IVirtualManifest, ManifestBuilderStrategy } from '../../builders';
import { VirtualElement, VirtualElementAPIType } from './VirtualElement';
import {
  IVirtualManifestBuilderProps,
  VirtualManifestBuilder,
} from './VirtualManifestBuilder';
import { VirtualManifestAPI } from '../VirtualManifestAPI';
import { DependenciesProvider } from './DependenciesProvider';
import { DependenciesContributor } from '../../dependencies';
import { OperationSource } from '../../panorama';
import { createOperationContext } from '../OperationContext';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export class VirtualElementProvider {
  #virtualManifestBuilder: VirtualManifestBuilder;
  #dependenciesProvider: DependenciesProvider;

  #virtualElementCache: Map<
    string,
    {
      element: VirtualElement<VirtualElementAPIType.VirtualManifestAPI>;
      editorPointers: EditorPointer<CompRef>[];
    }
  > = new Map();

  constructor(
    private readonly dependenciesContributor: DependenciesContributor,
    private readonly manifestStore: ManifestStore,
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly manifestChangeDetection: IManifestChangeDetection,
    private readonly manifestServiceFlags: ManifestServiceFlags,
    private readonly manifestServiceStats: ManifestServiceStats,
  ) {
    this.#dependenciesProvider = new DependenciesProvider(
      this.dependenciesContributor,
      this.editorPointerAPI,
      this.manifestStore,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );

    this.#virtualManifestBuilder = new VirtualManifestBuilder(
      this.dependenciesContributor,
      this.manifestStore,
      this.editorPointerAPI,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );

    this.#subscribeOnChangeDetection();
  }

  #subscribeOnChangeDetection() {
    this.manifestChangeDetection.subscribe((patch) => {
      if (ManifestChangeDetection.isGlobalPatch(patch)) {
        this.#virtualElementCache.clear();
      } else if (
        patch.type === ChangeType.Manifest ||
        patch.type === ChangeType.Invalidate ||
        patch.type === ChangeType.Props ||
        patch.type === ChangeType.Data
      ) {
        [...this.#virtualElementCache.entries()]
          .filter(([, { editorPointers, element }]) => {
            /**
             * we need to invalidate cache if data/props is changed
             * while component has container action.
             * see AbstractVirtualManifestBuilderWithActionableSlots logic
             */
            if (
              (patch.type === ChangeType.Props ||
                patch.type === ChangeType.Data) &&
              !element.getVirtualManifest().tree.manifest?.editorElement
                ?.actions?.container
            ) {
              return false;
            }

            return ManifestChangeDetection.isPatchMatchEditorPointers(
              this.editorPointerAPI,
              patch,
              editorPointers,
            );
          })
          .forEach(([key]) => {
            this.#virtualElementCache.delete(key);
          });
      }
    });
  }

  #normalizeEditorPointers(
    props: IVirtualManifestBuilderProps,
  ): EditorPointer<CompRef>[] {
    if (props.editorPointers) {
      return Array.isArray(props.editorPointers)
        ? props.editorPointers
        : [props.editorPointers];
    }

    return [];
  }

  getVirtualElement(virtualManifest: IVirtualManifest) {}

  buildVirtualElement(
    props: IVirtualManifestBuilderProps,
    source?: OperationSource,
  ): VirtualElement<VirtualElementAPIType.VirtualManifestAPI> {
    const editorPointers = this.#normalizeEditorPointers(props);
    const strategy = props.strategy ?? ManifestBuilderStrategy.Intersection;

    /**
     * optimization –
     * if we create a virtual manifest for a single editor pointer (it is 99% cases),
     * we build it always on ROOT component, and then shift the current pointer using
     * a child path, which is respected by ElementTraverseNode, so the final
     * manifest is resolved according to virtual root manifest plus child path
     */
    const shouldApplyChildPath =
      !props.contextBuilder &&
      editorPointers?.length === 1 &&
      !!this.editorPointerAPI.getInnerElementPath(editorPointers[0])?.length;

    const focusEditorPointer = shouldApplyChildPath
      ? this.editorPointerAPI.buildEditorPointer(
          this.editorPointerAPI.getDocumentPointer(editorPointers[0]),
        )
      : null;

    const resolvedEditorPointers = focusEditorPointer
      ? [focusEditorPointer]
      : editorPointers;

    const virtualManifestId =
      props.contextBuilder && !props.cacheNamespace
        ? VirtualManifestBuilder.generateHashVirtualManifestId()
        : VirtualManifestBuilder.generateVirtualManifestId(
            resolvedEditorPointers,
            strategy,
            props.cacheNamespace,
            source,
          );

    const shouldCache =
      !VirtualManifestBuilder.isHashGenerateVirtualManifestId(
        virtualManifestId,
      );

    if (shouldCache) {
      const cached = this.#virtualElementCache.get(virtualManifestId);

      if (cached) {
        if (focusEditorPointer) {
          const child = cached.element.getChild(
            this.editorPointerAPI.getInnerElementPath(editorPointers[0])!,
          );

          if (child) {
            return child;
          }

          return this.#buildFallbackVirtualElement(
            focusEditorPointer,
            editorPointers[0],
            virtualManifestId,
            source,
          );
        }

        return cached.element;
      }
    }

    const virtualManifest = this.#virtualManifestBuilder.buildVirtualManifest({
      ...props,
      editorPointers: resolvedEditorPointers,
    });

    const element = new VirtualElement(
      { ...virtualManifest, virtualManifestId },
      this.editorPointerAPI,
      {
        nodeAPIType: VirtualElementAPIType.VirtualManifestAPI,
        virtualElementProvider: this,
        operationContext: createOperationContext(source),
      },
    );

    if (shouldCache) {
      this.#virtualElementCache.set(virtualManifestId, {
        element,
        editorPointers: virtualManifest.manifests.map((x) => x.editorPointer),
      });
    }

    if (focusEditorPointer) {
      const child = element.getChild(
        this.editorPointerAPI.getInnerElementPath(editorPointers[0])!,
      );

      if (child) {
        return child;
      }

      return this.#buildFallbackVirtualElement(
        focusEditorPointer,
        editorPointers[0],
        virtualManifestId,
        source,
      );
    }

    return element;
  }

  createVirtualManifestAPI(
    node: VirtualElement<any>,
    isReadonly = false,
  ): VirtualManifestAPI {
    return new VirtualManifestAPI(
      node,
      this.#dependenciesProvider,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
      isReadonly,
    );
  }

  getManifestQueryAPI() {
    return this.#dependenciesProvider.getManifestQueryAPI();
  }

  /**
   * temporary fix to resolve element API for unexisting pointers,
   * when innerElementPath refs to unexisting child.
   */
  #buildFallbackVirtualElement(
    rootPointer: EditorPointer<CompRef>,
    focusPointer: EditorPointer<CompRef>,
    virtualManifestId: string | null,
    source: OperationSource | undefined,
  ): VirtualElement<VirtualElementAPIType.VirtualManifestAPI> {
    this.#printUnexistingChildError(rootPointer, focusPointer);

    const virtualManifest = this.#virtualManifestBuilder.buildVirtualManifest({
      contextBuilder: (builder) => {
        return builder.withEditorElement(rootPointer, {});
      },
    });

    return new VirtualElement(
      {
        ...virtualManifest,
        virtualManifestId:
          virtualManifestId ?? virtualManifest.virtualManifestId,
      },
      this.editorPointerAPI,
      {
        nodeAPIType: VirtualElementAPIType.VirtualManifestAPI,
        virtualElementProvider: this,
        operationContext: createOperationContext(source),
      },
    );
  }

  #printUnexistingChildError(
    rootPointer: EditorPointer<CompRef>,
    focusPointer: EditorPointer<CompRef>,
  ) {
    console.error(
      createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ManifestContextBuild,
      )
        .withMessage(
          'can not resolve child element, probably innerElementPath refs to unexisting child',
        )
        .withMessage(`root pointer – ${rootPointer.asUniqueKey}`)
        .withMessage(`focus pointer – ${focusPointer.asUniqueKey}`),
    );
  }
}
